import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, createUser, updateUser, deleteUser } from '../store/slices/usersSlice';
import { cyclesAPI } from '../services/api';
import toast from 'react-hot-toast';

const ROLE_OPTIONS = [
  { value: 'admin',      label: 'Admin'      },
  { value: 'hod',        label: 'HOD'        },
  { value: 'hr',         label: 'HR'         },
  { value: 'management', label: 'Management' },
];

const ROLE_COLORS = {
  hod:        { bg: '#EFF6FF', color: '#1D4ED8' },
  hr:         { bg: '#F0FFF4', color: '#15803D' },
  management: { bg: '#FFF7ED', color: '#C2410C' },
  admin:      { bg: '#F3F4F6', color: '#374151' },
};

const EMPTY_FORM = { name: '', email: '', mobile: '', password: '', role: 'admin', department: [], emp_id: '' };

/* ── Multi-select dept chip component ─────────────────────────────────────── */
function DeptMultiSelect({ value, options, onChange }) {
  // value = array of dept strings
  const toggle = (dept) => {
    if (value.includes(dept)) onChange(value.filter(d => d !== dept));
    else onChange([...value, dept]);
  };

  if (options.length === 0) {
    return <p style={{ fontSize: 12.5, color: 'var(--grey-400)', margin: 0 }}>No departments found. Sync Zoho first.</p>;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map(dept => {
        const selected = value.includes(dept);
        return (
          <button
            key={dept}
            type="button"
            onClick={() => toggle(dept)}
            style={{
              padding: '4px 10px', borderRadius: 20, fontSize: 12.5, cursor: 'pointer',
              border: selected ? '1.5px solid #1D4ED8' : '1.5px solid var(--grey-200)',
              background: selected ? '#EFF6FF' : 'var(--white)',
              color: selected ? '#1D4ED8' : 'var(--grey-500)',
              fontWeight: selected ? 600 : 400,
              transition: 'all 0.15s',
            }}
          >
            {selected && <span style={{ marginRight: 4 }}>✓</span>}{dept}
          </button>
        );
      })}
    </div>
  );
}

export default function UsersPage() {
  const dispatch = useDispatch();
  const { list: users, loading } = useSelector(s => s.users);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | 'edit'
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [departments, setDepartments] = useState([]);

  useEffect(() => { dispatch(fetchUsers()); }, [dispatch]);

  // Fetch departments once for the multi-select
  useEffect(() => {
    cyclesAPI.getDepartments()
      .then(r => setDepartments(r.data || []))
      .catch(() => {});
  }, []);

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  // Convert comma-sep dept string → array for form state
  const deptStrToArr = (str) => str ? str.split(',').map(d => d.trim()).filter(Boolean) : [];
  // Convert array → comma-sep for API
  const deptArrToStr = (arr) => (arr || []).join(', ');

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditId(null); setFormErrors({}); setModal('add');
  };
  const openEdit = (u) => {
    setForm({
      name: u.name,
      email: u.email,
      mobile: u.mobile || '',
      password: '',
      role: u.role || 'admin',
      department: deptStrToArr(u.department || ''),
      emp_id: u.emp_id || '',
    });
    setEditId(u.id); setFormErrors({}); setModal('edit');
  };
  const closeModal = () => setModal(null);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    if (modal === 'add' && !form.password) errs.password = 'Password is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    // Convert dept array to comma-sep string for API
    const deptStr = deptArrToStr(form.department);
    try {
      if (modal === 'add') {
        await dispatch(createUser({ ...form, department: deptStr })).unwrap();
        toast.success('User created successfully.');
      } else {
        const payload = {
          name: form.name, email: form.email, mobile: form.mobile,
          role: form.role, department: deptStr, emp_id: form.emp_id,
        };
        await dispatch(updateUser({ id: editId, data: payload })).unwrap();
        toast.success('User updated successfully.');
      }
      closeModal();
      dispatch(fetchUsers());
    } catch (err) {
      toast.error(err?.email?.[0] || err?.detail || 'Action failed.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.name}"?`)) return;
    try {
      await dispatch(deleteUser(u.id)).unwrap();
      toast.success('User deleted.');
    } catch { toast.error('Delete failed.'); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">User Management</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add New User
        </button>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--grey-100)' }}>
          <div className="search-input-wrap" style={{ maxWidth: 280 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="form-input" style={{ height: 36 }}
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}><span className="spinner" style={{ width: 32, height: 32 }} /></div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Role</th>
                <th>Department(s)</th>
                <th>Emp ID</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--grey-400)' }}>
                  {search ? 'No users match your search.' : 'No users found.'}
                </td></tr>
              ) : filtered.map(u => {
                const rc = ROLE_COLORS[u.role] || ROLE_COLORS.admin;
                return (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.mobile || '—'}</td>
                    <td>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11.5, fontWeight: 600, background: rc.bg, color: rc.color }}>
                        {(u.role || 'admin').toUpperCase()}
                      </span>
                    </td>
                    <td style={{ color: 'var(--grey-500)', fontSize: 12.5 }}>{u.department || '—'}</td>
                    <td style={{ color: 'var(--grey-500)', fontFamily: 'monospace', fontSize: 12 }}>{u.emp_id || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                        <button className="btn-icon magenta" title="Edit" onClick={() => openEdit(u)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(u)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/>
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'add' ? 'Add New User' : 'Edit User'}</span>
              <button className="btn-icon" onClick={closeModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { label: 'Name', key: 'name', type: 'text', placeholder: 'Full name' },
                { label: 'Email', key: 'email', type: 'email', placeholder: 'email@company.com' },
                { label: 'Mobile', key: 'mobile', type: 'tel', placeholder: '9999999999' },
                ...(modal === 'add' ? [{ label: 'Password', key: 'password', type: 'password', placeholder: '••••••••' }] : []),
              ].map(f => (
                <div key={f.key} className="form-group">
                  <label className="form-label">{f.label}</label>
                  <input
                    className={`form-input${formErrors[f.key] ? ' error' : ''}`}
                    type={f.type} placeholder={f.placeholder}
                    value={form[f.key] || ''}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  />
                  {formErrors[f.key] && <span className="form-error">{formErrors[f.key]}</span>}
                </div>
              ))}

              {/* Role */}
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-input" value={form.role}
                  onChange={e => setForm(p => ({
                    ...p,
                    role: e.target.value,
                    department: !['hod','hr'].includes(e.target.value) ? [] : p.department,
                    emp_id: !['hod','hr'].includes(e.target.value) ? '' : p.emp_id,
                  }))}>
                  {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {/* Department multi-select — only shown for HOD */}
              {['hod','hr'].includes(form.role) && (
                <div className="form-group">
                  <label className="form-label">
                    Department(s)
                    <span style={{ color: 'var(--grey-400)', fontWeight: 400, marginLeft: 6 }}>
                      (select all departments this HOD manages)
                    </span>
                  </label>
                  <DeptMultiSelect
                    value={form.department}
                    options={departments}
                    onChange={val => setForm(p => ({ ...p, department: val }))}
                  />
                  {form.department.length > 0 && (
                    <p style={{ fontSize: 11.5, color: 'var(--grey-400)', margin: '4px 0 0' }}>
                      Selected: {form.department.join(', ')}
                    </p>
                  )}
                </div>
              )}

              {/* Employee ID — only for HOD, used to exclude them from own recommendations */}
              {['hod','hr'].includes(form.role) && (
                <div className="form-group">
                  <label className="form-label">
                    Employee ID
                    <span style={{ color: 'var(--grey-400)', fontWeight: 400, marginLeft: 6 }}>
                      (optional — prevents HOD from recommending themselves)
                    </span>
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="e.g. EMP001"
                    value={form.emp_id}
                    onChange={e => setForm(p => ({ ...p, emp_id: e.target.value }))}
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
